import React, { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  TrendingDown as UndervalIcon,
  TrendingUp as OvervalIcon,
  CompareArrows as MismatchIcon,
  ArrowForward as ArrowIcon,
  ExpandMore,
  OpenInNew as LoadIcon,
  AutoFixHigh as FixIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  useLazyGetClusterAuditQuery,
  useReExtractDiscrepancyDataMutation,
  useUpdateDiscrepancyValuesMutation,
  useDeleteDiscrepancyMutation,
  useUpdateDiscrepancyReviewStatusMutation,
  useAddDiscrepancyCommentMutation,
  useRemoveDiscrepancyCommentMutation,
} from "@/services/customs-document/customs-document-api";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useSnackbar } from "@/hooks";
import DiscrepancyReviewControls, {
  type ReviewComment,
  type ReviewRecord,
  type ReviewStatus,
} from "./discrepancy-review-controls";
import { formatCurrencyPrecise, getSeverityConfig, itemToReviewRecord } from "./verification-helpers";
import { formatHTSCode } from "@/utils";
import SeverityDot from "@/components/severity-dot";
import { tokens, labelStyle } from "@/tokens";
import { tableContainerSx, tableCompactSx } from "@/components/table/table-styles";

/* ── Shared card style constants ── */
const cardSx = { border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.lg, overflow: "hidden" } as const;
const cardHeaderSx = { px: 2, py: 1.25, bgcolor: tokens.bg.hover, borderBottom: `1px solid ${tokens.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center" } as const;
const cardBodySx = { p: 2 } as const;
const sectionSx = { px: 3, pb: 2.5 } as const;
interface DiscrepancyDetailDialogProps {
  open: boolean;
  onClose: () => void;
  item: ValueDiscrepancyItem | null;
  onReExtractSuccess?: () => void;
  onDeleteSuccess?: () => void;
  onExited?: () => void;
}

const getTypeConfig = (type: string) => {
  switch (type) {
    case "UNDERVALUATION":
      return { icon: <UndervalIcon sx={{ fontSize: 16 }} />, color: tokens.status.danger, bg: tokens.status.dangerBg, label: "Undervaluation" };
    case "OVERVALUATION":
      return { icon: <OvervalIcon sx={{ fontSize: 16 }} />, color: tokens.status.warning, bg: tokens.status.warningBg, label: "Overvaluation" };
    default:
      return { icon: <MismatchIcon sx={{ fontSize: 16 }} />, color: tokens.text.secondary, bg: tokens.bg.muted, label: "Mismatch" };
  }
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case "HIGH": return tokens.status.danger;
    case "MEDIUM": return tokens.status.warning;
    case "LOW": return tokens.status.info;
    default: return tokens.text.secondary;
  }
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case "VALUE_MISMATCH": return "Value Mismatch";
    case "QUANTITY_MISMATCH": return "Quantity Mismatch";
    case "HTS_CLASSIFICATION": return "HTS Classification";
    case "COUNTRY_OF_ORIGIN": return "Country of Origin";
    case "MANUFACTURER_MISMATCH": return "Manufacturer Mismatch";
    case "MISSING_DOCUMENT": return "Missing Document";
    case "DATE_DISCREPANCY": return "Date Discrepancy";
    case "IDENTIFIER_MISMATCH": return "Identifier Mismatch";
    case "DUTY_CALCULATION": return "Duty Calculation";
    default: return category;
  }
};

const DiscrepancyDetailDialog: React.FC<DiscrepancyDetailDialogProps> = ({
  open,
  onClose,
  item,
  onReExtractSuccess,
  onDeleteSuccess,
  onExited,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const isTDAdmin = user?.userRole === roles.TDADMIN;
  const isSpOwner = user?.userRole === roles.SPOWNER;
  const tenantId = isTDAdmin && selectedTenant ? selectedTenant._id : undefined;
  const { show: showSnackbar } = useSnackbar();

  const canRemoveComment = useCallback((comment: ReviewComment) => {
    if (isTDAdmin || isSpOwner) return true;
    return !!user?._id && comment.authorId === user._id;
  }, [isTDAdmin, isSpOwner, user?._id]);

  const [getClusterAudit] = useLazyGetClusterAuditQuery();
  const [reExtractDiscrepancyData, { isLoading: isReExtracting }] = useReExtractDiscrepancyDataMutation();
  const [updateDiscrepancyValues, { isLoading: isUpdatingValues }] = useUpdateDiscrepancyValuesMutation();
  const [deleteDiscrepancy, { isLoading: isDeleting }] = useDeleteDiscrepancyMutation();
  const [updateReviewStatus, { isLoading: isStatusUpdating }] = useUpdateDiscrepancyReviewStatusMutation();
  const [addComment, { isLoading: isAddingComment }] = useAddDiscrepancyCommentMutation();
  const [removeComment] = useRemoveDiscrepancyCommentMutation();

  const [fullAudit, setFullAudit] = useState<ClusterAudit | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  // Local review state — updated immediately from mutation responses
  const [localReview, setLocalReview] = useState<ReviewRecord | null>(null);

  // Sync from item prop
  useEffect(() => {
    setLocalReview(item ? itemToReviewRecord(item) : null);
  }, [item]);

  // Manual edit state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDeclared, setEditDeclared] = useState("");
  const [editActual, setEditActual] = useState("");
  const [editLineNumber, setEditLineNumber] = useState("");

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isReviewUpdating = isStatusUpdating || isAddingComment;

  const handleReExtract = async () => {
    if (!item) return;
    try {
      const result = await reExtractDiscrepancyData({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        tenantId,
      }).unwrap();

      if (result.success) {
        showSnackbar("Success", `Extracted: ${result.discrepancyType} - $${result.discrepancyAmount?.toLocaleString()}`, "success");
        onReExtractSuccess?.();
        handleClose();
      } else {
        showSnackbar("Error", result.error || "Could not extract values from description", "error");
      }
    } catch (error) {
      showSnackbar("Error", "Failed to re-extract data", "error");
    }
  };

  const handleClose = () => {
    setFullAudit(null);
    setAuditError(null);
    setIsEditMode(false);
    setEditDeclared("");
    setEditActual("");
    setEditLineNumber("");
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleStartEdit = () => {
    if (!item) return;
    setEditDeclared(item.declaredValue?.toString() || "0");
    setEditActual(item.actualValue?.toString() || "0");
    // Populate with all existing lineNumbers joined by commas
    const lineNum = item.lineNumbers && item.lineNumbers.length > 0
      ? item.lineNumbers.join(", ")
      : item.lineNumber != null
        ? item.lineNumber.toString()
        : item.entryLine
          ? item.entryLine.split('-').pop() || ""
          : "";
    setEditLineNumber(lineNum);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditDeclared("");
    setEditActual("");
    setEditLineNumber("");
  };

  const handleSaveManualValues = async () => {
    if (!item) return;
    const declaredValue = parseFloat(editDeclared);
    const actualValue = parseFloat(editActual);

    if (isNaN(declaredValue) || isNaN(actualValue)) {
      showSnackbar("Error", "Please enter valid numeric values", "error");
      return;
    }

    // Parse comma-separated line numbers
    let lineNumbers: number[] | undefined;
    if (editLineNumber.trim()) {
      const parts = editLineNumber.split(",").map((s) => s.trim()).filter(Boolean);
      const parsed = parts.map((s) => parseInt(s, 10));
      if (parsed.some((n) => isNaN(n) || n < 1)) {
        showSnackbar("Error", "Please enter valid line numbers (positive integers, comma-separated)", "error");
        return;
      }
      lineNumbers = parsed;
    }

    try {
      const result = await updateDiscrepancyValues({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        declaredValue,
        actualValue,
        lineNumbers,
        tenantId,
      }).unwrap();

      if (result.success) {
        showSnackbar("Success", "Values updated successfully", "success");
        setIsEditMode(false);
        onReExtractSuccess?.();
        handleClose();
      } else {
        showSnackbar("Error", result.error || "Failed to update values", "error");
      }
    } catch (error) {
      showSnackbar("Error", "Failed to update values", "error");
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    try {
      const result = await deleteDiscrepancy({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        tenantId,
      }).unwrap();

      if (result.success) {
        showSnackbar("Success", "Discrepancy deleted", "success");
        onDeleteSuccess?.();
        handleClose();
      } else {
        showSnackbar("Error", result.error || "Failed to delete discrepancy", "error");
      }
    } catch (error) {
      showSnackbar("Error", "Failed to delete discrepancy", "error");
    }
  };

  const handleLoadAudit = async () => {
    if (!item) return;
    setAuditLoading(true);
    setAuditError(null);
    try {
      const result = await getClusterAudit({
        clusterId: item.clusterId,
        tenantId,
      }).unwrap();
      if (result) {
        setFullAudit(result);
      } else {
        setAuditError("No audit data found.");
      }
    } catch {
      setAuditError("Failed to load audit details.");
    } finally {
      setAuditLoading(false);
    }
  };

  const applyReviewResponse = useCallback((resp: DiscrepancyReviewResponse) => {
    setLocalReview({
      status: (resp.reviewStatus as ReviewStatus) || null,
      statusSetBy: resp.reviewStatusSetBy || null,
      statusSetAt: resp.reviewStatusSetAt || null,
      comments: (resp.reviewComments || []).map((c) => ({
        id: c.id,
        text: c.text,
        authorId: c.authorId,
        authorName: c.authorName,
        createdAt: c.createdAt,
      })),
    });
  }, []);

  const handleStatusChange = useCallback(async (status: ReviewStatus) => {
    if (!item) return;
    try {
      const resp = await updateReviewStatus({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        status,
        tenantId,
      }).unwrap();
      applyReviewResponse(resp);
    } catch {
      showSnackbar("Error", "Failed to update status", "error");
    }
  }, [item, updateReviewStatus, tenantId, applyReviewResponse, showSnackbar]);

  const handleAddComment = useCallback(async (text: string) => {
    if (!item) return;
    try {
      const resp = await addComment({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        text,
        tenantId,
      }).unwrap();
      applyReviewResponse(resp);
    } catch {
      showSnackbar("Error", "Failed to add comment", "error");
    }
  }, [item, addComment, tenantId, applyReviewResponse, showSnackbar]);

  const handleRemoveComment = useCallback(async (commentId: string) => {
    if (!item) return;
    try {
      const resp = await removeComment({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        commentId,
        tenantId,
      }).unwrap();
      applyReviewResponse(resp);
    } catch {
      showSnackbar("Error", "Failed to remove comment", "error");
    }
  }, [item, removeComment, tenantId, applyReviewResponse, showSnackbar]);

  const severityConfig = item ? getSeverityConfig(item.severity) : null;
  const typeConfig = item ? getTypeConfig(item.discrepancyType) : null;

  // Tokens matching the Title column of the main valuation table
  const hdr = { ...labelStyle, whiteSpace: "nowrap" } as const;
  const val = { fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.medium, color: tokens.text.body } as const;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      SlideProps={{ onExited: () => onExited?.() }}
    >
      {item && severityConfig && typeConfig ? (
      <Box sx={{ width: { xs: "100vw", sm: 900 }, maxWidth: "100vw", height: "100%", display: "flex", flexDirection: "column", bgcolor: tokens.bg.surface }}>

        {/* ── Header — matches table header row ── */}
        <Box sx={{ px: 2.5, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: tokens.bg.hover, borderBottom: `1px solid ${tokens.border.default}` }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
              Valuation Discrepancy
            </Typography>
            <SeverityDot severity={item.severity?.toLowerCase() as "high" | "medium" | "low"} />
            <Chip
              label={typeConfig.label}
              size="small"
              sx={{
                height: 20,
                fontSize: tokens.fontSize["2xs"],
                fontWeight: tokens.fontWeight.bold,
                borderRadius: 0.5,
                bgcolor: typeConfig.bg,
                color: typeConfig.color,
                border: `1px solid ${typeConfig.color}30`,
              }}
            />
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ color: tokens.text.muted }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* ── Scrollable content ── */}
        <Box sx={{ flex: 1, overflow: "auto" }}>

          {/* Value comparison — table-style rows, not cards.
              On xs the 5-column layout is too narrow to read, so we let the
              inner block scroll horizontally with a minWidth that keeps each
              cell readable. */}
          <Box sx={{ borderBottom: `1px solid ${tokens.border.light}`, overflowX: { xs: "auto", sm: "visible" } }}>
            <Box sx={{ minWidth: { xs: 480, sm: 0 } }}>
              {/* Header labels */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 24px 1fr 1fr 1fr", alignItems: "center", px: 2.5, py: 0.75, bgcolor: tokens.bg.hover, borderBottom: `1px solid ${tokens.border.default}` }}>
                <Typography sx={hdr}>Declared</Typography>
                <Box />
                <Typography sx={hdr}>Actual</Typography>
                <Typography sx={{ ...hdr, textAlign: "right" }}>Discrepancy</Typography>
                <Typography sx={{ ...hdr, textAlign: "right" }}>Est. Duty</Typography>
              </Box>
              {/* Values */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 24px 1fr 1fr 1fr", alignItems: "center", px: 2.5, py: 1.5 }}>
                <Box>
                  <Typography sx={{ ...val, fontWeight: tokens.fontWeight.book }}>{formatCurrencyPrecise(item.declaredValue)}</Typography>
                  {item.declaredSource && <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontWeight: tokens.fontWeight.normal, mt: 0.25 }}>{item.declaredSource}</Typography>}
                </Box>
                <ArrowIcon sx={{ color: tokens.text.disabled, fontSize: 14 }} />
                <Box>
                  <Typography sx={{ ...val, fontWeight: tokens.fontWeight.book }}>{formatCurrencyPrecise(item.actualValue)}</Typography>
                  {item.actualSource && <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontWeight: tokens.fontWeight.normal, mt: 0.25 }}>{item.actualSource}</Typography>}
                </Box>
                <Typography sx={{ ...val, color: tokens.status.danger, textAlign: "right" }}>{formatCurrencyPrecise(item.discrepancyAmount)}</Typography>
                <Typography sx={{ ...val, color: (item.estimatedDutyImpact ?? 0) > 0 ? tokens.status.success : tokens.text.subtle, textAlign: "right" }}>
                  {(item.estimatedDutyImpact ?? 0) > 0 ? formatCurrencyPrecise(item.estimatedDutyImpact) : "-"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Action buttons */}
          {isEditMode ? (
            <Box sx={{ mb: 2, px: 2.5, pt: 1.5 }}>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }} flexWrap="wrap">
                <TextField label="Declared Value" type="number" value={editDeclared} onChange={(e) => setEditDeclared(e.target.value)} size="small" sx={{ width: 160 }} InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: tokens.text.secondary }}>$</Typography> }} />
                <TextField label="Actual Value" type="number" value={editActual} onChange={(e) => setEditActual(e.target.value)} size="small" sx={{ width: 160 }} InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: tokens.text.secondary }}>$</Typography> }} />
                <TextField label="Line Numbers" type="text" value={editLineNumber} onChange={(e) => setEditLineNumber(e.target.value)} size="small" sx={{ width: 140 }} placeholder="e.g. 1, 2, 3" />
                {item.entryLineData && item.entryLineData.length > 0 && (
                  <Button variant="outlined" size="small" onClick={() => setEditLineNumber(item.entryLineData!.map((l) => l.lineNumber).join(", "))} sx={{ fontWeight: tokens.fontWeight.bold, minWidth: "auto", px: 1.5, height: 40, color: tokens.brand.primary, borderColor: tokens.brand.primary, "&:hover": { borderColor: tokens.brand.primaryHover, bgcolor: tokens.status.successBg } }}>All</Button>
                )}
              </Stack>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button variant="outlined" onClick={handleCancelEdit} disabled={isUpdatingValues} sx={{ borderRadius: tokens.radius.md, color: tokens.text.body, borderColor: tokens.border.default }}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveManualValues} disabled={isUpdatingValues} sx={{ bgcolor: tokens.brand.primary, "&:hover": { bgcolor: tokens.brand.primaryHover }, fontWeight: tokens.fontWeight.bold, borderRadius: tokens.radius.md }}>
                  {isUpdatingValues ? <CircularProgress size={20} color="inherit" /> : "Save"}
                </Button>
              </Stack>
            </Box>
          ) : showDeleteConfirm ? (
            <Box sx={{ textAlign: "center", mb: 2, px: 2.5, pt: 1.5 }}>
              <Typography sx={{ mb: 1, color: tokens.status.danger, fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2 }}>Delete this discrepancy?</Typography>
              <Typography sx={{ mb: 1.5, color: tokens.text.secondary, fontSize: tokens.fontSize.xs }}>This will remove it from the audit permanently.</Typography>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button variant="outlined" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} sx={{ borderRadius: tokens.radius.md, color: tokens.text.body, borderColor: tokens.border.default }}>Cancel</Button>
                <Button variant="contained" onClick={handleDelete} disabled={isDeleting} sx={{ bgcolor: tokens.status.danger, "&:hover": { bgcolor: tokens.status.dangerHover }, fontWeight: tokens.fontWeight.bold, borderRadius: tokens.radius.md }}>
                  {isDeleting ? <CircularProgress size={20} color="inherit" /> : "Delete"}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mb: 2, px: 2.5, pt: 1.5 }}>
              <Button
                size="small"
                startIcon={isReExtracting ? <CircularProgress size={14} color="inherit" /> : <FixIcon sx={{ fontSize: 15 }} />}
                onClick={handleReExtract}
                disabled={isReExtracting}
                sx={{ fontSize: tokens.fontSize.sm2, textTransform: "none", color: tokens.text.body, fontWeight: tokens.fontWeight.semibold, "&:hover": { bgcolor: tokens.bg.hover } }}
              >
                {isReExtracting ? "Extracting..." : "Fix Extraction"}
              </Button>
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                onClick={handleStartEdit}
                sx={{ fontSize: tokens.fontSize.sm2, textTransform: "none", color: tokens.text.body, fontWeight: tokens.fontWeight.semibold, "&:hover": { bgcolor: tokens.bg.hover } }}
              >
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
                onClick={() => setShowDeleteConfirm(true)}
                sx={{ fontSize: tokens.fontSize.sm2, textTransform: "none", color: tokens.status.danger, fontWeight: tokens.fontWeight.semibold, "&:hover": { bgcolor: tokens.status.dangerBg } }}
              >
                Delete
              </Button>
            </Stack>
          )}

          {/* ── Comparison Hero Cards ── */}
          <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1, border: `1px solid ${isEditMode ? tokens.brand.primary : tokens.border.default}`, borderRadius: tokens.radius.lg, p: 2, textAlign: "center", transition: "border-color 0.15s" }}>
                <Typography sx={hdr}>Declared</Typography>
                {isEditMode ? (
                  <TextField type="number" value={editDeclared} onChange={(e) => setEditDeclared(e.target.value)} size="small" sx={{ mt: 0.5, width: "100%", "& input": { textAlign: "center", fontSize: "1.25rem", fontWeight: tokens.fontWeight.heavy } }} InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: tokens.text.secondary }}>$</Typography> }} />
                ) : (
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, mt: 0.5 }}>
                    {formatCurrencyPrecise(item.declaredValue)}
                  </Typography>
                )}
                {!isEditMode && item.declaredSource && <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>{item.declaredSource}</Typography>}
              </Box>
              <ArrowIcon sx={{ color: tokens.text.disabled, fontSize: 20, flexShrink: 0 }} />
              <Box sx={{ flex: 1, border: `1px solid ${isEditMode ? tokens.brand.primary : tokens.border.default}`, borderRadius: tokens.radius.lg, p: 2, textAlign: "center", transition: "border-color 0.15s" }}>
                <Typography sx={hdr}>Actual</Typography>
                {isEditMode ? (
                  <TextField type="number" value={editActual} onChange={(e) => setEditActual(e.target.value)} size="small" sx={{ mt: 0.5, width: "100%", "& input": { textAlign: "center", fontSize: "1.25rem", fontWeight: tokens.fontWeight.heavy } }} InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: tokens.text.secondary }}>$</Typography> }} />
                ) : (
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, mt: 0.5 }}>
                    {formatCurrencyPrecise(item.actualValue)}
                  </Typography>
                )}
                {!isEditMode && item.actualSource && <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>{item.actualSource}</Typography>}
              </Box>
            </Stack>

            {/* Edit mode: line number field below cards */}
            {isEditMode && (
              <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} justifyContent="center" alignItems="center">
                <TextField label="Line Numbers" type="text" value={editLineNumber} onChange={(e) => setEditLineNumber(e.target.value)} size="small" sx={{ width: 160 }} placeholder="e.g. 1, 2, 3" />
                {item.entryLineData && item.entryLineData.length > 0 && (
                  <Button variant="outlined" size="small" onClick={() => setEditLineNumber(item.entryLineData!.map((l) => l.lineNumber).join(", "))} sx={{ fontWeight: tokens.fontWeight.bold, minWidth: "auto", px: 1.5, height: 40, color: tokens.brand.primary, borderColor: tokens.brand.primary, "&:hover": { borderColor: tokens.brand.primaryHover, bgcolor: tokens.status.successBg } }}>All</Button>
                )}
              </Stack>
            )}

            {/* Discrepancy + Add'l Duty */}
            <Stack direction="row" spacing={3} sx={{ mt: 2, justifyContent: "center" }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={hdr}>Discrepancy</Typography>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: tokens.fontWeight.heavy, color: tokens.status.danger, mt: 0.25 }}>
                  {formatCurrencyPrecise(item.discrepancyAmount)}
                </Typography>
              </Box>
              {(item.estimatedDutyImpact ?? 0) > 0 && (
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={hdr}>Add'l Duty</Typography>
                  <Typography sx={{ fontSize: "1.1rem", fontWeight: tokens.fontWeight.heavy, color: tokens.status.success, mt: 0.25 }}>
                    {formatCurrencyPrecise(item.estimatedDutyImpact)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* ── Entry Details Card ── */}
          <Box sx={sectionSx}>
            <Box sx={cardSx}>
              <Box sx={cardHeaderSx}>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>
                  Entry Details
                </Typography>
              </Box>
              <Box sx={cardBodySx}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" }, gap: 1.5 }}>
                  {[
                    { l: "Entry #", v: item.entryNumber || "N/A" },
                    { l: "Entry Date", v: item.entryDate ? new Date(item.entryDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A" },
                    { l: "Line", v: item.lineNumbers?.length ? item.lineNumbers.join(", ") : item.lineNumber != null ? String(item.lineNumber) : item.entryLine ? item.entryLine.split("-").pop() || "N/A" : "N/A" },
                    ...(item.confidenceScore !== undefined ? [{ l: "Confidence", v: `${item.confidenceScore}%` }] : []),
                    { l: "Audit Status", v: item.auditStatus === "APPROVED" ? "Approved" : item.auditStatus === "NEEDS_REVIEW" ? "Needs Review" : "Discrepancies Found" },
                    { l: "Audited", v: item.auditDate ? new Date(item.auditDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A" },
                    ...(item.auditUpdatedAt ? [{ l: "Updated", v: new Date(item.auditUpdatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }] : []),
                  ].map((meta) => (
                    <Box key={meta.l}>
                      <Typography sx={hdr}>{meta.l}</Typography>
                      <Typography sx={{ ...val, mt: 0.25 }}>{meta.v}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Entry Lines Card ── */}
          {item.entryLineData && item.entryLineData.length > 0 && (
            <Box sx={{ mb: 2, px: 2.5, pt: 2 }}>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1 }}>
                Entry Lines ({item.entryLineData.length})
              </Typography>
              <Box sx={tableContainerSx}>
                <Box sx={tableCompactSx}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Line #</TableCell>
                        <TableCell>HTS Code</TableCell>
                        <TableCell>Ch.99</TableCell>
                        <TableCell>Mfg ID</TableCell>
                        <TableCell>Origin</TableCell>
                        <TableCell align="right">Goods Value</TableCell>
                        <TableCell align="right">Duty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.entryLineData.map((line) => {
                        const isAffected =
                          (item.lineNumbers || []).includes(line.lineNumber) ||
                          (item.entryLines || []).some((el) => el.toLowerCase() === (line.entryLine || "").toLowerCase());
                        return (
                          <TableRow key={line.lineNumber} sx={{ ...(isAffected && { bgcolor: "#FEF2F2 !important" }) }}>
                            <TableCell sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {line.lineNumber}
                                {line.hasSec232 && <Chip label="232" size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5, bgcolor: tokens.status.infoBg, color: tokens.chip.blue.text }} />}
                                {isAffected && <Chip label="Affected" size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5, bgcolor: tokens.status.dangerBg, color: tokens.status.danger }} />}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2 }}>{line.htsCode ? formatHTSCode(line.htsCode) : "-"}</TableCell>
                            <TableCell>
                              {line.chapter99Codes && line.chapter99Codes.length > 0 ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {line.chapter99Codes.map((code) => (
                                    <Chip key={code} label={code} size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5, bgcolor: tokens.bg.muted, color: tokens.text.body }} />
                                  ))}
                                </Box>
                              ) : <Typography sx={{ fontSize: "inherit", color: tokens.text.subtle }}>-</Typography>}
                            </TableCell>
                            <TableCell sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2 }}>{line.manufacturerId || "-"}</TableCell>
                            <TableCell sx={{ color: tokens.text.secondary }}>{line.countryOfOrigin || "-"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2 }}>{line.goodsValue != null ? formatCurrencyPrecise(line.goodsValue) : "-"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2, color: tokens.status.success }}>{line.lineDutyAmount != null ? formatCurrencyPrecise(line.lineDutyAmount) : "-"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Box>
          )}

          {/* ── Metadata ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1.5, mb: 2, px: 2.5 }}>
            {[
              { l: "Entry #", v: item.entryNumber || "N/A" },
              { l: "Entry Date", v: item.entryDate ? new Date(item.entryDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A" },
              { l: "Line", v: item.lineNumbers?.length ? item.lineNumbers.join(", ") : item.lineNumber != null ? String(item.lineNumber) : item.entryLine ? item.entryLine.split("-").pop() || "N/A" : "N/A" },
              ...(item.confidenceScore !== undefined ? [{ l: "Confidence", v: `${item.confidenceScore}%` }] : []),
              { l: "Audit Status", v: item.auditStatus === "APPROVED" ? "Approved" : item.auditStatus === "NEEDS_REVIEW" ? "Needs Review" : "Discrepancies Found" },
              { l: "Audited", v: item.auditDate ? new Date(item.auditDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A" },
              ...(item.auditUpdatedAt ? [{ l: "Updated", v: new Date(item.auditUpdatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }] : []),
            ].map((meta) => (
              <Box key={meta.l}>
                <Typography sx={hdr}>{meta.l}</Typography>
                <Typography sx={{ ...val, mt: 0.25 }}>{meta.v}</Typography>
              </Box>
            ))}
          </Box>

          {/* ── Paid duties ── */}
          {item.paidDuties && (
            <Box sx={{ mb: 2, mx: 2.5, p: 1.5, bgcolor: tokens.bg.hover, borderRadius: tokens.radius.lg }}>
              <Typography sx={{ ...hdr, mb: 1 }}>
                What Was Paid (ACE){(item.lineNumbers?.length ?? 0) > 1 ? ` — Lines ${item.lineNumbers!.join(", ")}` : ""}
              </Typography>
              {[
                { label: "MFN Duty", value: item.paidDuties.mfnDuty },
                { label: "Section 201", value: item.paidDuties.duty201 },
                { label: "Section 232", value: item.paidDuties.duty232 },
                { label: "Section 301", value: item.paidDuties.duty301 },
                { label: "Other Ch.99", value: item.paidDuties.oth99Duties },
              ].filter((r) => (r.value ?? 0) > 0).map((r, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
                  <Typography sx={val}>{r.label}</Typography>
                  <Typography sx={{ ...val, fontVariantNumeric: "tabular-nums" }}>{formatCurrencyPrecise(r.value)}</Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, mt: 0.5, borderTop: `1px solid ${tokens.border.default}` }}>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>Total Duty</Typography>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", color: tokens.text.primary }}>{formatCurrencyPrecise(item.paidDuties.totalDuty)}</Typography>
              </Stack>
            </Box>
          )}

          {/* Metal split */}
          {item.metalPercentage != null && (
            <Box sx={{ mb: 2, mx: 2.5, p: 1.5, bgcolor: "#FFF7ED", borderRadius: tokens.radius.lg, border: "1px solid #FED7AA" }}>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: "#9A3412", fontWeight: tokens.fontWeight.bold, mb: 0.5 }}>Metal Split Detected</Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={val}>Metal Percentage (from value split)</Typography>
                <Typography sx={{ ...val, fontWeight: tokens.fontWeight.bold }}>{(item.metalPercentage * 100).toFixed(1)}%</Typography>
              </Stack>
            </Box>
          )}

          {/* Description */}
          {item.description && (
            <Box sx={{ mb: 2, px: 2.5 }}>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontWeight: tokens.fontWeight.normal, maxWidth: 600 }}>
                {item.description}
              </Typography>
            </Box>
          )}

          {/* Recommendation */}
          {item.recommendation && (
            <Box sx={{ mx: 2.5, p: 1.5, bgcolor: tokens.status.successBg, borderRadius: tokens.radius.lg, mb: 2 }}>
              <Typography sx={{ ...hdr, color: "#065F46", fontWeight: tokens.fontWeight.bold, mb: 0.5 }}>Recommendation</Typography>
              <Typography sx={{ ...val, color: tokens.text.excerpt }}>{item.recommendation}</Typography>
            </Box>
          )}

          {/* ── Load Full Audit ── */}
          <Box sx={{ mb: 2, px: 2.5 }}>
            {!fullAudit && !auditLoading && (
              <Button
                variant="outlined"
                startIcon={<LoadIcon sx={{ fontSize: 16 }} />}
                onClick={handleLoadAudit}
                sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md, px: 2.5, py: 1, color: tokens.text.body, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
              >
                Load Full Audit
              </Button>
            )}
            {auditLoading && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CircularProgress size={18} />
                <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.secondary }}>Loading audit details...</Typography>
              </Stack>
            )}
            {auditError && <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.status.danger }}>{auditError}</Typography>}

            {fullAudit && (
              <Box>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 2 }}>Full Audit Details</Typography>

                {fullAudit.discrepancies && fullAudit.discrepancies.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ ...hdr, mb: 1 }}>Discrepancies ({fullAudit.discrepancies.length})</Typography>
                    {fullAudit.discrepancies.map((discrepancy, idx) => (
                      <Accordion key={discrepancy.id || idx} defaultExpanded={idx === 0} sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: `${tokens.radius.lg} !important`, mb: 1, "&:before": { display: "none" }, boxShadow: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: `${getSeverityColor(discrepancy.severity)}08`, borderRadius: tokens.radius.lg }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                            <SeverityDot severity={discrepancy.severity?.toLowerCase() as "high" | "medium" | "low"} showLabel={false} size={16} />
                            <Chip label={getCategoryLabel(discrepancy.category)} size="small" variant="outlined" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5 }} />
                            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.medium, flex: 1, color: tokens.text.body }}>{discrepancy.title}</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 2 }}>
                          <Typography sx={{ ...val, mb: 2 }}>{discrepancy.description}</Typography>
                          {discrepancy.citations && discrepancy.citations.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography sx={{ ...hdr, mb: 1 }}>Sources</Typography>
                              {discrepancy.citations.map((citation, cidx) => (
                                <Box key={cidx} sx={{ p: 1.5, mb: 1, bgcolor: tokens.bg.hover, borderRadius: tokens.radius.sm, borderLeft: `3px solid ${tokens.brand.primary}` }}>
                                  <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                                    <Chip label={citation.documentName || "Document"} size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5 }} />
                                    {citation.documentType && <Chip label={citation.documentType} size="small" variant="outlined" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5 }} />}
                                    {citation.tradeDataLine && <Chip label={`Line: ${citation.tradeDataLine}`} size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5, bgcolor: "#E0E7FF", color: "#3730A3" }} />}
                                  </Box>
                                  {citation.excerpt && <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, fontStyle: "italic", mt: 0.5 }}>&ldquo;{citation.excerpt}&rdquo;</Typography>}
                                </Box>
                              ))}
                            </Box>
                          )}
                          {discrepancy.recommendation && (
                            <Box sx={{ p: 1.5, bgcolor: tokens.status.successBg, borderRadius: tokens.radius.md, border: `1px solid ${tokens.status.success}30` }}>
                              <Typography sx={{ ...hdr, color: tokens.chip.green.text, fontWeight: tokens.fontWeight.bold }}>Recommendation</Typography>
                              <Typography sx={{ ...val, color: tokens.text.excerpt, mt: 0.5 }}>{discrepancy.recommendation}</Typography>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}

                    {fullAudit.analysisText && (
                      <Accordion sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: `${tokens.radius.lg} !important`, mb: 1, "&:before": { display: "none" }, boxShadow: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>Full Analysis</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography sx={{ ...val, whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{fullAudit.analysisText}</Typography>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {fullAudit.thinkingTrail && fullAudit.thinkingTrail.length > 0 && (
                      <Accordion sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: `${tokens.radius.lg} !important`, "&:before": { display: "none" }, boxShadow: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>Reasoning Steps ({fullAudit.thinkingTrail.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box component="ol" sx={{ pl: 2, m: 0 }}>
                            {fullAudit.thinkingTrail.map((step, idx) => (
                              <Box component="li" key={idx} sx={{ mb: 1, color: tokens.text.secondary, fontSize: tokens.fontSize.xs }}>{step}</Box>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
              </Box>
            )}
          </Box>

          <Box sx={{ borderTop: `1px solid ${tokens.border.light}` }} />

          {/* ── Review Controls ── */}
          <Box sx={{ px: 2.5, py: 2 }}>
          <DiscrepancyReviewControls
            review={localReview}
            onStatusChange={handleStatusChange}
            onAddComment={handleAddComment}
            onRemoveComment={handleRemoveComment}
            canRemoveComment={canRemoveComment}
            isUpdating={isReviewUpdating}
          />
          </Box>
        </Box>

        {/* ── Footer ── */}
        <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${tokens.border.default}`, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md, px: 3, py: 0.75, color: tokens.text.body, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
          >
            Close
          </Button>
        </Box>
      </Box>
      ) : null}
    </Drawer>
  );
};

export default DiscrepancyDetailDialog;
